import React, { useCallback, useEffect, useRef, useState } from "react";
import { t } from "../i18n";
import { AlertIcon, AlertTriangleIcon, BellIcon, SuccessIcon, XIcon } from "./Icons";

// Всплывающие уведомления панели — стопкой в правом верхнем углу (на телефоне —
// сверху во всю ширину). Новое выезжает сверху и встаёт первым, остальные
// плавно сдвигаются вниз; закрытое уезжает вверх, и стопка так же плавно
// смыкается. Оформление и цвета — классами kl-toast-* в index.css: так их
// перекрашивает светлая тема, а тон события задаётся одной переменной.
//
// Время жизни считает сам тост: пока на нём курсор, отсчёт стоит (вместе с
// полоской внизу), чтобы длинный текст успели дочитать.

const VARIANT_ICON = {
  success: SuccessIcon,
  error: AlertIcon,
  warning: AlertTriangleIcon,
  info: BellIcon
};

// Длительность ухода — та же, что у анимации kl-toast-leave в index.css.
const LEAVE_MS = 320;

const ToastItem = ({ toast, onDismiss }) => {
  const variant = VARIANT_ICON[toast?.variant] ? toast.variant : "info";
  const Icon = VARIANT_ICON[variant];
  const durationMs = Number(toast?.durationMs) > 0 ? Number(toast.durationMs) : 7200;
  const [paused, setPaused] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const remainingRef = useRef(durationMs);
  const leavingRef = useRef(false);
  const onDismissRef = useRef(onDismiss);
  onDismissRef.current = onDismiss;
  const toastId = toast?.id;

  const leave = useCallback(() => {
    if (leavingRef.current) return;
    leavingRef.current = true;
    setLeaving(true);
    window.setTimeout(() => onDismissRef.current?.(toastId), LEAVE_MS);
  }, [toastId]);

  // Отсчёт идёт, пока тост не на паузе; при паузе остаток запоминается.
  useEffect(() => {
    if (paused || leaving) return undefined;
    const startedAt = Date.now();
    const handle = window.setTimeout(leave, Math.max(0, remainingRef.current));
    return () => {
      window.clearTimeout(handle);
      remainingRef.current -= Date.now() - startedAt;
    };
  }, [paused, leaving, leave]);

  return (
    <div className={`kl-toast-slot${leaving ? " is-leaving" : ""}`}>
      <div className="kl-toast-slot-inner">
        <div
          className={`kl-toast is-${variant}${paused ? " is-paused" : ""}`}
          role={variant === "error" ? "alert" : "status"}
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
        >
          <span className="kl-toast-icon" aria-hidden="true">
            <Icon size={17} />
          </span>
          <div className="kl-toast-text">
            {toast?.title && <div className="kl-toast-title">{toast.title}</div>}
            {toast?.body && <div className="kl-toast-body">{toast.body}</div>}
          </div>
          <button
            type="button"
            className="kl-toast-close"
            onClick={leave}
            aria-label={t("Закрыть")}
          >
            <XIcon size={14} />
          </button>
          <span
            className="kl-toast-progress"
            style={{ animationDuration: `${durationMs}ms` }}
            aria-hidden="true"
          />
        </div>
      </div>
    </div>
  );
};

const ToastStack = ({ toasts, onDismiss, isPhoneView = false, zIndex }) => {
  const list = Array.isArray(toasts) ? toasts : [];
  if (!list.length) return null;
  // Свежее — сверху: оно выезжает первым, остальные уходят вниз.
  const ordered = [...list].reverse();
  return (
    <div
      className={`kl-toast-stack${isPhoneView ? " is-phone" : ""}`}
      style={zIndex ? { zIndex } : undefined}
      aria-live="polite"
    >
      {ordered.map((toast) => (
        <ToastItem key={toast.id} toast={toast} onDismiss={onDismiss} />
      ))}
    </div>
  );
};

export default ToastStack;
